import { useEffect } from 'react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { Col, CustomInput, Row } from 'reactstrap'
import Button from 'components/Button'
import ModalDialog from 'components/ModalDialog'
import { SupportMessageThreadStatus } from 'interfaces/message'
import routes from 'routes'
import styles from './supportThreadFilter.module.scss'

export enum ProcessPublishStatus {
  ALL = 0,
  DRAFT = 1,
  PUBLISHED = 2
}

interface ISupportThreadFilterProps {
  isOpen: boolean
  toggle: () => void
  setPage: (page: number) => void
}

const SupportThreadFilter = ({ isOpen, toggle, setPage }: ISupportThreadFilterProps) => {
  const { messageStore } = useStores()
  const { supportThreadFilter } = messageStore
  const { status, publish } = supportThreadFilter
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const persistedStatus = params.get('status') || status
  const persistedPublish = params.get('publish') || publish
  const defaultValues = {
    status: `${persistedStatus}`,
    publish: `${persistedPublish}`
  }
  const navigate = useNavigate()
  const { handleSubmit, register, reset } = useForm({ defaultValues })

  useEffect(() => {
    reset(defaultValues)
  }, [supportThreadFilter, persistedStatus, persistedPublish])

  useEffect(() => {
    messageStore.setSupportThreadFilter(+persistedStatus, +persistedPublish)
  }, [persistedStatus, persistedPublish])

  function onSubmit(data: any) {
    const { status = SupportMessageThreadStatus.ALL, publish = ProcessPublishStatus.ALL } = data
    messageStore.setSupportThreadFilter(+status, +publish)
    params.set('status', `${status}`)
    params.set('publish', `${publish}`)
    toggle()
    setPage(1)
    navigate(`${routes.messages.value}?${params.toString()}`)
  }

  function handleCancel() {
    toggle()
  }

  const { ref: refStatus, ...registerStatus } = register('status')
  const { ref: refPublish, ...registerPublish } = register('publish')

  return (
    <ModalDialog
      backdrop={true}
      headerClassName={styles.header}
      className={styles.modal}
      isOpen={isOpen}
      toggle={toggle}
      title="Filter and Sorting Settings"
    >
      <Row className={styles.container}>
        <Col sm={6}>
          <Row className={styles.subTitle}>Status</Row>
          <Row>
            <Col className={styles.status}>
              <CustomInput
                type="radio"
                label={`All threads`}
                id="all"
                value={SupportMessageThreadStatus.ALL}
                innerRef={refStatus}
                {...registerStatus}
                className={styles.radio}
              />
              <CustomInput
                color="red"
                type="radio"
                label={`Unclaimed`}
                id="unclaimed"
                value={SupportMessageThreadStatus.UNCLAIMED}
                style={{ color: 'red' }}
                innerRef={refStatus}
                {...registerStatus}
                className={cx(styles.radio, styles.red)}
              />
              <CustomInput
                type="radio"
                label={`Claimed`}
                style={{ color: '#00A9EB' }}
                id="claimed"
                value={SupportMessageThreadStatus.CLAIMED}
                innerRef={refStatus}
                {...registerStatus}
                className={cx(styles.radio, styles.blue)}
              />
              <CustomInput
                type="radio"
                label={`Resolved`}
                id="resolved"
                value={SupportMessageThreadStatus.RESOLVED}
                innerRef={refStatus}
                {...registerStatus}
                className={styles.radio}
              />
              <CustomInput
                type="radio"
                label={`Unread`}
                id="unread"
                value={SupportMessageThreadStatus.UNREAD}
                innerRef={refStatus}
                {...registerStatus}
                className={styles.radio}
              />
            </Col>
          </Row>
        </Col>
        <Col sm={6}>
          <Row className={styles.subTitle}>Support message thread</Row>
          <Col>
            <CustomInput
              type="radio"
              label={`All message types`}
              id="allPublish"
              value={ProcessPublishStatus.ALL}
              innerRef={refPublish}
              {...registerPublish}
              className={styles.radio}
            />
            <CustomInput
              type="radio"
              label={`Published`}
              id="published"
              value={ProcessPublishStatus.PUBLISHED}
              innerRef={refPublish}
              {...registerPublish}
              className={styles.radio}
            />
            <CustomInput
              type="radio"
              label={`Drafts`}
              id="draft"
              value={ProcessPublishStatus.DRAFT}
              innerRef={refPublish}
              {...registerPublish}
              className={styles.radio}
            />
          </Col>
        </Col>
      </Row>
      <Row className={styles.buttonRow}>
        <div className={styles.actionButtonWrapper}>
          <Button outline className={styles.cancelBtn} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            color="info"
            className={styles.newBtn}
            backgroundColor="var(--current-primary-color)"
            onClick={handleSubmit(onSubmit)}
          >
            Apply
          </Button>
        </div>
      </Row>
    </ModalDialog>
  )
}

export default observer(SupportThreadFilter)
